import type { DriverStatus, RideStatus } from '../enums/status';
import type { GeoPoint } from '../geo/monaco';
import type { AppNotification, Ride, RideEvent, RideOffer } from './index';

/**
 * Contrat des événements temps réel (WebSocket).
 *
 * Le même vocabulaire est utilisé par la passerelle Socket.IO du serveur node
 * classique et par le mécanisme de polling de secours utilisé en Lambda, afin que
 * le front ne connaisse qu'une seule forme d'événement.
 */
export enum RealtimeEvent {
	/** Course mise à jour (tout changement de statut ou de contenu). */
	RIDE_UPDATED = 'ride:updated',
	/** Position du chauffeur assigné à la course suivie. */
	RIDE_DRIVER_POSITION = 'ride:driver-position',
	/** Nouvelle proposition envoyée à un chauffeur. */
	OFFER_RECEIVED = 'offer:received',
	/** Proposition close (perdue, expirée, annulée) : retirer la modale. */
	OFFER_CLOSED = 'offer:closed',
	/** Position d'un chauffeur, diffusée à la carte live du back-office. */
	DRIVER_POSITION = 'driver:position',
	/** Changement de disponibilité d'un chauffeur. */
	DRIVER_STATUS = 'driver:status',
	/** Notification applicative à afficher. */
	NOTIFICATION = 'notification',
	/** La course anonyme a été reprise sur un mobile : la borne peut se réinitialiser. */
	KIOSK_RIDE_CLAIMED = 'kiosk:ride-claimed',
	/** Entrée de journal, diffusée au fil de l'eau au back-office. */
	RIDE_EVENT = 'ride:event',
}

export interface RideUpdatedPayload {
	ride: Ride;
	previousStatus: RideStatus | null;
}

export interface RideDriverPositionPayload {
	rideId: number;
	driverId: number;
	position: GeoPoint;
	etaSeconds: number | null;
	distanceMeters: number | null;
}

export interface OfferReceivedPayload {
	offer: RideOffer;
	ride: Ride;
	/** Secondes restantes pour répondre au moment de l'émission. */
	ttlSeconds: number;
}

export interface OfferClosedPayload {
	offerId: number;
	rideId: number;
	reason: 'lost' | 'expired' | 'cancelled' | 'answered';
}

export interface DriverPositionPayload {
	driverId: number;
	position: GeoPoint;
	status: DriverStatus;
	rideId: number | null;
}

export interface DriverStatusPayload {
	driverId: number;
	status: DriverStatus;
	previousStatus: DriverStatus;
}

export interface NotificationPayload {
	notification: AppNotification;
}

export interface KioskRideClaimedPayload {
	rideId: number;
	kioskId: number;
}

/**
 * Événement de journal diffusé en direct.
 *
 * Alimente le fil d'exploitation : l'agent voit les commandes, acceptations et
 * prises en charge arriver au fil de l'eau, sans avoir à ouvrir chaque course.
 */
export interface RideEventPayload {
	event: RideEvent;
	/**
	 * Course concernée, réduite à ce qu'affiche le fil.
	 *
	 * Le chauffeur y figure — visage et plaque — parce que c'est à cela qu'un
	 * régulateur reconnaît une ligne : « c'est la course de Marc », pas
	 * « la course 1 843 ».
	 */
	ride: Pick<Ride, 'id' | 'publicId' | 'status' | 'source' | 'pickup' | 'dropoff'> & {
		driver?: RideEventDriver | null;
		client?: RideEventClient | null;
	};
}

/** Chauffeur tel qu'il apparaît dans le fil d'exploitation. */
export interface RideEventDriver {
	id: number;
	firstName: string;
	lastName: string;
	avatarUrl: string | null;
	plateNumber: string | null;
}

/**
 * Demandeur de la course, tel qu'il apparaît dans le fil.
 *
 * Une course qui vient d'être commandée n'a pas encore de chauffeur : c'est le
 * client qu'il faut montrer. Sans compte, il n'y a qu'un prénom saisi à la
 * borne — l'avatar retombe alors sur les initiales.
 */
export interface RideEventClient {
	id: number | null;
	firstName: string;
	lastName: string;
	avatarUrl: string | null;
	/** Vrai pour une commande passée sans compte. */
	isGuest: boolean;
}

export interface RealtimeEventMap {
	[RealtimeEvent.RIDE_UPDATED]: RideUpdatedPayload;
	[RealtimeEvent.RIDE_DRIVER_POSITION]: RideDriverPositionPayload;
	[RealtimeEvent.OFFER_RECEIVED]: OfferReceivedPayload;
	[RealtimeEvent.OFFER_CLOSED]: OfferClosedPayload;
	[RealtimeEvent.DRIVER_POSITION]: DriverPositionPayload;
	[RealtimeEvent.DRIVER_STATUS]: DriverStatusPayload;
	[RealtimeEvent.NOTIFICATION]: NotificationPayload;
	[RealtimeEvent.KIOSK_RIDE_CLAIMED]: KioskRideClaimedPayload;
	[RealtimeEvent.RIDE_EVENT]: RideEventPayload;
}

/**
 * Salons temps réel.
 *
 * Un client ne peut rejoindre que les salons que ses droits autorisent — la
 * vérification est faite côté passerelle à la souscription, jamais côté front.
 */
export const RealtimeRoom = {
	ride: (rideId: number | string): string => `ride:${rideId}`,
	driver: (driverId: number | string): string => `driver:${driverId}`,
	user: (userId: number | string): string => `user:${userId}`,
	kiosk: (kioskId: number | string): string => `kiosk:${kioskId}`,
	/** Salon de supervision : carte live et flux de courses du back-office. */
	backoffice: (): string => 'backoffice',
} as const;
